import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Briefcase, Users, Network, MessageSquare, UserCircle, Crown, Shield, Globe2, Megaphone, X, LifeBuoy } from "lucide-react";
import { useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { ProfileNudge } from "@/components/ProfileStrength";
import InstallBanner from "@/components/InstallBanner";

const NAV = [
  { to: "/community", label: "Community", icon: Globe2, id: "nav-community-link" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, id: "nav-jobs-link" },
  { to: "/people", label: "People", icon: Users, id: "nav-people-link" },
  { to: "/network", label: "Network", icon: Network, id: "nav-network-link" },
  { to: "/messages", label: "Messages", icon: MessageSquare, id: "nav-messenger-link" },
  { to: "/profile", label: "Profile", icon: UserCircle, id: "nav-profile-link" },
];

function useNotifications() {
  const api = useApi();
  const [count, setCount] = useState({ unread_messages: 0, pending_invites: 0 });
  useEffect(() => {
    let off = false;
    const tick = () => api("get", "/notifications").then((d) => !off && setCount(d)).catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => { off = true; clearInterval(t); };
  }, [api]);
  return count;
}

export const Logo = ({ dark }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
    <svg width="26" height="26" viewBox="0 0 44 44" className={dark ? "text-white" : "text-slate-900"}>
      {[0, 120, 240].map((r) => <path key={r} d="M22 22 L24 3 Q22 1 20 3 Z" fill="currentColor" transform={`rotate(${r} 22 22)`} />)}
      <circle cx="22" cy="22" r="3.5" fill="#F59E0B" />
    </svg>
    <span className={`font-extrabold tracking-tight text-lg ${dark ? "text-white" : "text-slate-900"}`}>WindWork<span className="text-sky-600">Hub</span></span>
  </Link>
);

const Badge = ({ n, id }) => n > 0 ? (
  <span data-testid={id} className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold flex items-center justify-center">{n > 99 ? "99+" : n}</span>
) : null;

function Announcement() {
  const api = useApi();
  const [a, setA] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => { api("get", "/announcement").then(setA).catch((e) => console.error("Announcement load failed:", e)); }, [api]);
  if (!a || hidden || localStorage.getItem("ann-dismissed") === a.id) return null;
  return (
    <div className="bg-slate-900 text-white" data-testid="announcement-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-start gap-3 text-sm">
        <Megaphone size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="flex-1 whitespace-pre-line" data-testid="announcement-text">{a.text}</p>
        <button onClick={() => { localStorage.setItem("ann-dismissed", a.id); setHidden(true); }} aria-label="Dismiss" data-testid="announcement-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
}

function AppFooter() {
  const cls = "hover:text-slate-900 transition-colors";
  return (
    <footer className="border-t border-slate-200 mt-6" data-testid="app-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
        <Link to="/help" className="flex items-center gap-1 font-semibold text-sky-700 hover:text-sky-900" data-testid="footer-help-link"><LifeBuoy size={14} />Help – message the admin</Link>
        <Link to="/contact" className={cls} data-testid="footer-contact-link">Contact</Link>
        <Link to="/terms" className={cls} data-testid="footer-terms-link">Terms</Link>
        <Link to="/privacy" className={cls} data-testid="footer-privacy-link">Privacy</Link>
        <span className="ml-auto">© WindWorkHub</span>
      </div>
    </footer>
  );
}

export default function AppLayout({ children }) {
  const notif = useNotifications();
  const badge = (to) => to === "/messages" ? <Badge n={notif.unread_messages} id="nav-unread-badge" />
    : to === "/network" ? <Badge n={notif.pending_invites} id="nav-invites-badge" /> : null;
  const { me } = useMe();
  const linkCls = ({ isActive }) => `relative flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.id}>
                <span className="relative"><n.icon size={16} />{badge(n.to)}</span><span className="hidden lg:inline">{n.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden lg:block eyebrow" data-testid="nav-role-label">{me?.role === "company" ? "Company" : "Technician"}</span>
            {me?.access_role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin-link" className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700"><Shield size={14} /><span className="hidden sm:inline">Admin</span></NavLink>
            )}
            <NavLink to="/vip" data-testid="nav-vip-link" className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-bold whitespace-nowrap ${me?.is_vip ? "bg-amber-400 text-slate-950" : "border border-amber-400 text-amber-700 hover:bg-amber-50"}`}>
              <Crown size={14} />{me?.is_vip ? "VIP" : "Get VIP"}
            </NavLink>
            <UserButton>
              <UserButton.MenuItems>
                <UserButton.Link label="Help" labelIcon={<LifeBuoy size={15} />} href="/help" />
              </UserButton.MenuItems>
            </UserButton>
          </div>
        </div>
      </header>
      <Announcement />
      <ProfileNudge />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">{children}</main>
      <AppFooter />
      <InstallBanner />
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-6">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`m-${n.id}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium ${isActive ? "text-sky-700" : "text-slate-500"}`}>
            <span className="relative"><n.icon size={20} />{badge(n.to)}</span>{n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
